import type * as CLT from '@design-system/component-library';

export interface NotificationData {
  items: CLT.NotificationProps[];
}

export interface NotificationStore {
  getNotifications: () => NotificationData;
  addNotification: (text: string, type?: CLT.NotificationProps['type']) => void;
  addNotificationWithoutTimer: (
    notification: Pick<CLT.NotificationProps, 'id' | 'text' | 'type'>,
    onNotificationHide?: CLT.NotificationProps['onNotificationHide']
  ) => void;
  removeNotification: (notificationId: string) => void;
  clearNotifications: () => void;
  subscribe: (listener: () => void) => () => void;
}

/**
 * Creates an isolated notification store instance.
 * Use this in SSR to create a fresh store per request and avoid cross-request leakage.
 *
 * If SSR loaders need to trigger notifications, pass the per-request store via
 * createStaticHandler's context option so loaders can access it.
 */
export const createNotificationStore = (): NotificationStore => {
  let state: NotificationData = { items: [] };
  const listeners = new Set<() => void>();
  let idCounter = 0;

  const emit = () => listeners.forEach(listener => listener());

  const createNotification = (notification: Pick<CLT.NotificationProps, 'text' | 'type'>): CLT.NotificationProps => ({
    ...notification,
    id: (idCounter++).toString(),
    timer: 10,
    hasCloseButton: true,
  });

  const createNotificationWithoutTimer = (
    notification: Pick<CLT.NotificationProps, 'id' | 'text' | 'type'>
  ): CLT.NotificationProps => ({
    ...notification,
    hasCloseButton: true,
  });

  return {
    getNotifications: () => state,
    addNotification: (text, type = 'success') => {
      state = { items: [...state.items, createNotification({ text, type })] };
      emit();
    },
    addNotificationWithoutTimer: (notification, onNotificationHide) => {
      const item = { ...createNotificationWithoutTimer(notification), onNotificationHide };
      state = { items: [...state.items, item] };
      emit();
    },
    removeNotification: notificationId => {
      state = { items: state.items.filter(notification => notification.id !== notificationId) };
      emit();
    },
    clearNotifications: () => {
      state = { items: [] };
      emit();
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

// Default instance for client-side use (safe: one user per browser tab)
const defaultStore = createNotificationStore();

// Re-export individual functions from the default store so existing consumers don't need changes
export const getNotifications = defaultStore.getNotifications;
export const addNotification = defaultStore.addNotification;
export const addNotificationWithoutTimer = defaultStore.addNotificationWithoutTimer;
export const removeNotification = defaultStore.removeNotification;
export const clearNotifications = defaultStore.clearNotifications;
export const subscribeNotifications = defaultStore.subscribe;
